import { CountryWithMCC } from '@/src/types/country'
import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react'

// Dynamic baseUrl: use local file in development, hosted URL in production
const getUrl = () => {
    const isDevelopment = process.env.NODE_ENV === 'development'
    return isDevelopment ? '/json/countrylist.json' : 'https://ui.cltp.co/json/countrylist.json'
}

const staggeredBaseQueryWithRetry = retry(
    fetchBaseQuery({
        baseUrl: '',
        prepareHeaders: headers => {
            headers.set('Accept', 'application/json')
            headers.set('Content-Type', 'application/json')
            headers.set('Cache-Control', 'no-cache')
            return headers
        },
        // Handle malformed JSON responses
        responseHandler: async response => {
            const text = await response.text()
            return text
        }
    }),
    {
        maxRetries: 2
    }
)

export const countryApi = createApi({
    reducerPath: 'countryApi',
    baseQuery: staggeredBaseQueryWithRetry,
    tagTypes: ['CountryData'],
    endpoints: builder => ({
        getCountryData: builder.query<CountryWithMCC[], void>({
            query: getUrl,
            providesTags: ['CountryData'],
            transformResponse: (response: string) => {
                const TOP_SEARCH_COUNTRIES_CODE = ['SA', 'EG', 'IN', 'AE', 'KW', 'OM', 'QA', 'BH']
                const data = JSON.parse(response)
                const mappedData = data.map((item: CountryWithMCC) => ({
                    ...item,
                    category: TOP_SEARCH_COUNTRIES_CODE.includes(item.countryCode) ? 'topSearches' : 'others' //topSearches and others string is from messages/en.json and messages/ar.json
                }))

                // Sort by TOP_SEARCH_COUNTRIES_CODE order, with top search countries first
                return mappedData.sort(
                    (a: CountryWithMCC & { category: string }, b: CountryWithMCC & { category: string }) => {
                        const aIndex = TOP_SEARCH_COUNTRIES_CODE.indexOf(a.countryCode)
                        const bIndex = TOP_SEARCH_COUNTRIES_CODE.indexOf(b.countryCode)

                        // If both are in top search countries, sort by their order in the array
                        if (aIndex !== -1 && bIndex !== -1) {
                            return aIndex - bIndex
                        }
                        // If only a is in top search countries, a comes first
                        if (aIndex !== -1 && bIndex === -1) {
                            return -1
                        }
                        // If only b is in top search countries, b comes first
                        if (aIndex === -1 && bIndex !== -1) {
                            return 1
                        }
                        // If neither is in top search countries, maintain original order
                        return 0
                    }
                )
            }
        })
    })
})

// Export hooks for usage in components
export const { useGetCountryDataQuery, useLazyGetCountryDataQuery } = countryApi

// Export the API slice for store configuration
export default countryApi

// Export types for use in other files
export type { CountryWithMCC }
